<template>
  <div class="min-h-screen">
    <SiteHeader :page-name="'Project List'" />
    <div class="flex flex-col md:flex-row">
      <div class="flex flex-row p-3 gap-2">
        <SearchBar v-model:search-text="search" :show-refresh="false" @input="filterProjects"></SearchBar>
        <SelectButton v-if="!isClient(NBH)" class="m-auto h-10 md:mx-0 w-1/3" v-model="state" :options="states" @change="stateChanged" />
      </div>
      <div class="flex flex-row p-3 gap-2 md:m-auto md:mr-5 md:w-fit">
        <SingleReportButton v-if="isInspector" :report-id="'InspectionRequests'" :label="'Download All Worksheets'" :classes="'w-auto h-9 mt-auto'" />
        <Button class="h-9">Upload Files</Button>
      </div>
    </div>
    <DataTable
      :value="projectsFiltered"
      class="text-sm mx-4 py-3"
      paginator
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      :row-class="rowClass"
      v-model:selection="selectedProject"
      selectionMode="single"
      dataKey="id"
      removableSort
      striped-rows
      size="small"
      @row-select="projectSelected"
      footer="test"
    >
      <Column field="projectCode" header="ID" :footer="isClient(NBH) ? '' : 'Total'" footer-class="italic" sortable style="width: 10%">
        <template #body="{ data }">{{
          !isClient(NBH) || !entityIsInspector(data.entityType) ? data.projectCode : maskProjectCode(data.projectCode)
        }}</template>
      </Column>
      <Column field="projectName" header="Name" sortable style="width: 20%"></Column>
      <Column v-if="!showAction" field="propertyAddress" header="Address" sortable style="width: 10%"></Column>
      <Column field="currentDraw" header-class="text-center!" body-class="text-center" sortable style="width: 5%">
        <template #header>
          <p class="mx-auto">Draw</p>
        </template>
      </Column>
      <Column field="drawStatus" body-class="text-center!" sortable style="width: 5%">
        <template #header>
          <p class="mx-auto">Status</p>
        </template>

        <template #body="{ data }">
          <Tag class="text-center" :severity="getDrawStatusSeverity(data.drawStatus)">{{ getDrawStatusDisplayText(data.drawStatus) }}</Tag></template
        >
      </Column>
      <Column v-if="isInspector && !showAction" field="inspReq" header="Inspection Requested" sortable style="width: 5%"></Column>
      <Column v-if="!showAction" field="budget" header="Budget" :footer="budgetTotal" footer-class="italic" sortable style="width: 15%">
        <template #body="{ data }">{{ formatCurrency(data.budget) }}</template>
      </Column>
      <Column v-if="!showAction" field="drawnToDate" header="$ To Date" :footer="drawnTotal" footer-class="italic" sortable style="width: 15%">
        <template #body="{ data }">{{ formatCurrency(data.drawnToDate) }}</template>
      </Column>
      <Column field="fundsAvailable" header="Available" :footer="availableTotal" footer-class="italic" sortable style="width: 15%">
        <template #body="{ data }">{{ formatCurrency(data.fundsAvailable) }}</template>
      </Column>
      <Column v-if="isInspector" style="width: 10%">
        <template #header>
          <p class="mx-auto">Download</p>
        </template>
        <template #body="{ data }">
          <SingleReportButton
            v-if="entityIsInspector(data.entityType)"
            :report-id="'InspectionRequest'"
            :label="'Download Worksheet'"
            :project-id="data.projectId"
            classes="w-44 h-8 text-sm border-primary-400"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import { ProjectSummaryPortalListSchema, type ProjectSummary } from "@/types/Projects";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";
import { DrawStatus, getDrawStatusDisplayText, getDrawStatusSeverity } from "@/enums/DrawStatus";
import { ProjectRole, entityIsInspector } from "@/enums/ProjectRole";
import { isClient, NBH } from "@/services/Client";
import { formatCurrency, maskProjectCode } from "@/services/Utility";
import { getUserPermissions } from "@/services/Permissions";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { useAuthStore } from "@/stores/auth";

//TODO: Flags field?
//TODO: Change to ag-grid

const router = useRouter();
const lastState = ref("Active");
const state = ref("Active");
const states = ref(["Active", "Sold"]);

const isInspector = ref(false);
const showAction = ref(false);
const budgetTotal = ref<string>("");
const drawnTotal = ref<string>("");
const availableTotal = ref<string>("");
const projects = ref<ProjectSummary[]>([]);
const projectsFiltered = ref<ProjectSummary[]>([]);
const search = ref("");
const selectedProject = ref();

const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));
const userStore = useAuthStore();

onMounted(async (): Promise<void> => {
  await axios
    .get("/projects")
    .then((response) => {
      projects.value = ProjectSummaryPortalListSchema.parse(response.data.result);
      projectsFiltered.value = ProjectSummaryPortalListSchema.parse(response.data.result);
    })
    .catch((e) => errorApi(e));

  isInspector.value = projects.value.filter((project) => entityIsInspector(project.entityType)).length > 0;

  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));

  filterProjects();
});

const stateChanged = (): void => {
  if (state.value === null) state.value = lastState.value;
  lastState.value = state.value;
  filterProjects();
};

const filterProjects = (): void => {
  projectsFiltered.value = projects.value.filter((project) => {
    let show: boolean;

    if (state.value === "Active") {
      show = project.projectStatus.category === ProjectStatusCategory.ACTIVE || project.projectStatus.category === ProjectStatusCategory.PENDING;
    } else {
      show = project.projectStatus.category === ProjectStatusCategory.SOLD;
    }

    if (show && search.value !== "") {
      show =
        project.projectCode.toLowerCase().includes(search.value.toLowerCase()) ||
        project.projectName.toLowerCase().includes(search.value.toLowerCase()) ||
        project.propertyAddress.toLowerCase().includes(search.value.toLowerCase());
    }

    return show;
  });

  let budget = 0;
  let available = 0;
  let drawn = 0;
  for (let i = 0; i < projectsFiltered.value.length; i++) {
    const project = projectsFiltered.value[i];
    budget += project.budget;
    drawn += project.drawnToDate;
    available += project.fundsAvailable;
  }

  budgetTotal.value = formatCurrency(budget);
  drawnTotal.value = formatCurrency(drawn);
  availableTotal.value = formatCurrency(available);
};

const projectSelected = (): void => {
  router.push({
    name: "portal-project",
    params: {
      id: selectedProject.value.projectId,
    },
  });
};

const rowClass = (data: ProjectSummary) => {
  return [
    {
      "bg-danger-200! hover:bg-danger-300! dark:bg-danger-800! dark:hover:bg-danger-700!": data.locked,
      "bg-primary-200! hover:bg-primary-300! dark:bg-primary-800! dark:hover:bg-primary-700!":
        data.drawStatus === DrawStatus.INSPECTED || data.projectStatus.name === DrawStatus.DRAWN,
      "bg-warning-200! hover:bg-warning-300! dark:bg-warning-800! dark:hover:bg-warning-700!":
        data.drawStatus === DrawStatus.DECLINED && data.entityType !== ProjectRole.INSPECTOR,
    },
  ];
};
</script>
